import { useEffect, useRef, useState } from 'react';
import Api from '../api/global.service';

const POLL_INTERVAL_MS = 5000;

/**
 *
 * @param {object} ciclo            Ciclo seleccionado ({ id, nombre })
 * @param {boolean} active          Si false, no consulta (modal cerrado)
 *
 * @returns {{
 *   estado: object|null,           // mismo shape que devolvía el SSE:
 *                                  // { orquestador, paso_1_fichas, ..., total_fichas, total_evaluaciones }
 *   refrescar: () => void,         // reinicia el polling — necesario después de disparar una
 *                                  // nueva descarga (ej. otra agrupación) cuando el hook ya
 *                                  // había dejado de consultar por un estado terminal previo
 * }}
 */
const useOrquestadorSse = ({ ciclo, active }) => {
  const [estado, setEstado] = useState(null);

  const showRef        = useRef(active);
  const cicloRef        = useRef(ciclo);
  const timeoutRef      = useRef(null);
  const terminalRef     = useRef(false);
  const consultaIdRef   = useRef(0);

  useEffect(() => { showRef.current  = active; }, [active]);
  useEffect(() => { cicloRef.current = ciclo; }, [ciclo]);

  useEffect(() => {
    if (active && ciclo?.id) {
      terminalRef.current = false;
      consultar();
    } else {
      detener();
      setEstado(null);
    }
    return () => detener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ciclo?.id]);

  const detener = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Invalida cualquier respuesta en vuelo de una consulta anterior.
    consultaIdRef.current++;
  };

  const consultar = async () => {
    if (!showRef.current || !cicloRef.current?.id) return;

    const miConsultaId = ++consultaIdRef.current;

    try {
      const res = await Api.getAll('admin/sic/fichas/orquestador-estado', {
        id_ciclo: cicloRef.current.id,
      });

      if (miConsultaId !== consultaIdRef.current || !showRef.current) return;

      const data = res.data;
      setEstado(data);

      if (data?.orquestador && ['completo', 'detenido', 'error'].includes(data.orquestador.estado)) {
        terminalRef.current = true;
      }
    } catch (err) {
      if (miConsultaId !== consultaIdRef.current) return;
      // Error de red puntual — se reintenta en el siguiente tick igual.
    }

    if (miConsultaId === consultaIdRef.current && showRef.current && !terminalRef.current) {
      timeoutRef.current = setTimeout(consultar, POLL_INTERVAL_MS);
    }
  };

  // Una vez que el orquestador llega a un estado terminal, consultar() deja
  // de programar el siguiente tick (línea de arriba) para no seguir pegándole
  // al endpoint sin necesidad. Si después se dispara una nueva descarga (otra
  // agrupación) sobre el MISMO montaje del modal, hay que reactivar el
  // polling explícitamente — nada más lo hace automáticamente.
  const refrescar = () => {
    terminalRef.current = false;
    detener();
    consultar();
  };

  return { estado, refrescar };
};

export default useOrquestadorSse;
